// Vine growth (GEN-10 extension / MB.2-adjacent). PURE (ENG-1).
//
// The M11.1 crop model ages a block in place, and MB.2's `verticalGrow` stacks a
// plant UP off the ground with a small cap. Neither describes a vine: a vine is a
// run of identical segments that extends from its TIP, in a direction the species
// picks, off an anchor that is not the plant itself — and the nether's two grow in
// opposite directions off different materials. That is the mechanic this module
// adds, and weeping and twisting vines are its two instances.
//
// The direction is the whole design. `dir: -1` hangs down from a ceiling (weeping
// vines off a crimson canopy); `dir: +1` climbs up off a floor (twisting vines out
// of warped nylium). Everything else — where the run ends, what counts as the tip,
// how long it may get — falls out of that one field, so a third vine is a table
// entry rather than new code.
import { VINE } from '../config';
import type { Cell, GrowthView } from './growth';

/** One vine species: which way it runs, what it may root on, and how far it goes. */
export interface VineKind {
  /** +1 climbs up off a floor; −1 hangs down from a ceiling. */
  dir: 1 | -1;
  /** Blocks the FIRST segment may root on (the cell opposite `dir`). */
  anchors: ReadonlySet<string>;
  /** Longest run of segments, counted from the anchor. */
  cap: number;
  /** Per random-tick chance the tip extends one segment. */
  chance: number;
}

/** What a crimson canopy or the nether roof will hold a weeping vine on. */
const WEEPING_ANCHORS = new Set([
  'nether_wart_block', 'shroomlight', 'crimson_nylium', 'crimson_stem', 'netherrack',
]);
/** What twisting vines climb out of. */
const TWISTING_ANCHORS = new Set([
  'warped_nylium', 'warped_wart_block', 'shroomlight', 'warped_stem', 'netherrack', 'soul_soil',
]);

export const VINES: Readonly<Record<string, VineKind>> = Object.freeze({
  weeping_vines: { dir: -1, anchors: WEEPING_ANCHORS, cap: VINE.MAX_LENGTH, chance: VINE.GROW_CHANCE },
  twisting_vines: { dir: 1, anchors: TWISTING_ANCHORS, cap: VINE.MAX_LENGTH, chance: VINE.GROW_CHANCE },
});

/** Is `block` a vine this module grows? */
export function isVine(block: string | null | undefined): boolean {
  return block != null && block in VINES;
}

/**
 * Length of the run this segment belongs to, counted BACK against the growth
 * direction to the anchor. A lone segment is 1.
 */
export function vineLength(view: GrowthView, x: number, y: number, z: number, block: string): number {
  const kind = VINES[block];
  if (!kind) return 0;
  let n = 1;
  while (n <= kind.cap && view.name(x, y - kind.dir * n, z) === block) n++;
  return n;
}

/**
 * Can a vine of this species be rooted at (x,y,z)? True when the cell opposite the
 * growth direction holds either an anchor block or another segment of the same vine
 * — the test a placement and the first generated strand both need.
 */
export function vineAttaches(view: GrowthView, x: number, y: number, z: number, block: string): boolean {
  const kind = VINES[block];
  if (!kind) return false;
  const behind = view.name(x, y - kind.dir, z);
  return behind === block || (behind !== null && kind.anchors.has(behind));
}

/**
 * Extend a vine one segment, or null when it can't this tick. Only the TIP grows
 * (the cell ahead must be air), only while the run is under its cap, only off a
 * real anchor, and only when the roll passes.
 */
export function vineGrow(
  view: GrowthView, x: number, y: number, z: number, block: string, roll: number,
): Cell | null {
  const kind = VINES[block];
  if (!kind) return null;
  const ahead = y + kind.dir;
  if (view.name(x, ahead, z) !== 'air') return null;   // not the tip, or blocked
  if (!vineAttaches(view, x, y, z, block)) return null; // floating — nothing to grow off
  if (roll >= kind.chance) return null;
  if (vineLength(view, x, y, z, block) >= kind.cap) return null;
  return { x, y: ahead, z };
}

/**
 * Bone meal on a vine adds a whole burst at once rather than a single segment, so
 * it reads as fertiliser rather than a hurried random tick. Returns the cells to
 * fill, tip-first, stopping at the first blockage or the cap.
 */
export function vineBoneMeal(
  view: GrowthView, x: number, y: number, z: number, block: string, roll: number,
): Cell[] {
  const kind = VINES[block];
  if (!kind || !vineAttaches(view, x, y, z, block)) return [];
  const span = VINE.BONE_MEAL_MAX - VINE.BONE_MEAL_MIN + 1;
  const want = VINE.BONE_MEAL_MIN + Math.min(span - 1, Math.floor(roll * span));
  const room = kind.cap - vineLength(view, x, y, z, block);
  const out: Cell[] = [];
  for (let i = 1; i <= Math.min(want, room); i++) {
    const cy = y + kind.dir * i;
    if (view.name(x, cy, z) !== 'air') break;
    out.push({ x, y: cy, z });
  }
  return out;
}
